import { useEffect, useMemo, useState, useRef } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate, T } from '@tolgee/react';
import * as PropTypes from 'prop-types';
import {
  getInvitesForCurrentPage,
  changeRowsNumber,
  searchForInvites,
  deleteInvite,
  getInvites
} from '../../../redux/slices/accounts/invites/invites';
import { Button, Modal, OverlayTrigger, Stack, Tooltip } from 'react-bootstrap';
import Symbol from '../../utils/symbol';
import RoleIndicator from '../../utils/roleIndicator';
import SearchField from '../../utils/datatable/searchField';
import IsLoadingIndicator from '../../isLoadingIndicator';
import { fetchSubscriptionData, updateTeamMembersCount } from '../../../redux/slices/subscriptionManagement/subscriptionManagement';
import { PATH_PAGE } from '../../../routes/paths';
import { useNavigate } from 'react-router-dom';
import { Toaster, toast } from 'react-hot-toast';
import { useLocales } from '../../../locales';

export function InvitesTable() {
  const { currentLang } = useLocales();
  const navigate = useNavigate();
  const inviteDeletedCallback = () => {
    // refresh page
    toast.success(t('accounts.invites.table.invite_deleted'), {
      position: 'top-right',
      id: 'accounts.invites.table.invite_deleted'
    });
    dispatch(fetchSubscriptionData());
  };

  const { hasSocialMediaManagementSubscription, hasCloudSubscription } =
    useSelector((state) => state.userDetails);
  const dispatch = useDispatch();
  const { t } = useTranslate();
  const columns = [
    {
      name: t('accounts.invites.table.name'),
      show: true,
      key: 'email',
      sortable: true,
      grow: 2,
      selector: (row) => (
        <Stack
          direction={'horizontal'}
          gap={3}
          style={{ width: 300, padding: 4 }}
        >
          <div
            className="cursor-pointer"
            onClick={() =>
              navigate(PATH_PAGE.edit_invite.replace(':uuid', row.uuid))
            }
          >
            <Symbol initials={`${row.email[0] || 'U'}${row.email[1] || 'N'}`} />
          </div>

          <div className="d-flex justify-content-start flex-column">
            <span className="text-gray-400 fw-bold d-block fs-7">
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    <span className="text-gray-400 fw-bold d-block fs-7">
                      {row.email}
                    </span>
                  </Tooltip>
                }
              >
                <span className="text-gray-400 fw-bold d-block fs-7">
                  {row.email.length > 30
                    ? row.email.slice(0, 28) + '...'
                    : row.email}
                </span>
              </OverlayTrigger>
            </span>
          </div>
        </Stack>
      )
    },
    {
      name: t('accounts.invites.table.cockpit_role'),
      show: hasSocialMediaManagementSubscription,
      key: 'role',
      sortable: true,
      selector: (row) => (
        <div>{row.role ? <RoleIndicator role={row.role} /> : '-'}</div>
      )
    },
    {
      name: t('accounts.invites.table.cockpits'),
      show: hasSocialMediaManagementSubscription,
      selector: (row) => (
        <div>{`${
          row.role
            ? row.location_uuid_list && row.location_uuid_list?.length !== 0
              ? `${row.location_uuid_list?.length} ${t(
                  'accounts.invites.table.selected'
                )}`
              : t('accounts.invites.table.all')
            : '-'
        }`}</div>
      )
    },
    {
      name: t('accounts.invites.table.cloud_role'),
      show: hasCloudSubscription,
      sortable: true,
      key: 'cloud_user_role',
      selector: (row) => (
        <div>
          {row.cloud_user_role ? (
            <RoleIndicator role={row.cloud_user_role} />
          ) : (
            '-'
          )}
        </div>
      )
    },
    {
      name: t('accounts.invites.table.clouds'),
      show: hasCloudSubscription,
      selector: (row) => (
        <div>{`${
          row.cloud_user_role
            ? row.cloud_uuid_list && row.cloud_uuid_list?.length !== 0
              ? `${row.cloud_uuid_list?.length} ${t(
                  'accounts.invites.table.selected'
                )}`
              : t('accounts.invites.table.all')
            : '-'
        }`}</div>
      )
    },
    {
      name: t('accounts.team.table.created_at'),
      key: 'created',
      sortable: true,
      show: true,
      selector: (row) => (
        <div>
          {new Date(row.created).toLocaleString(currentLang.systemValue, {
            year: '2-digit',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric'
          })}
        </div>
      )
    },
    {
      name: t('accounts.invites.table.action'),
      show: true,
      selector: (row) => {
        const [show, setShow] = useState(false);
        const handleDelete = () => {
          dispatch(deleteInvite(row.uuid, pageNumber, inviteDeletedCallback));
          setShow(false);
        };
        return (
          <>
            <Modal show={show} onHide={() => {}} size="sm" centered>
              <Modal.Header className="border-0">
                <Modal.Title>
                  {' '}
                  {t('accounts.invites.table.confirm')}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="border-0">
                {t('accounts.invites.table.delete_invite_confirmation')}
              </Modal.Body>
              <Modal.Footer className="border-0">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShow(false)}
                >
                  {t('accounts.invites.table.close')}
                </Button>
                <Button variant="danger" size="sm" onClick={handleDelete}>
                  {t('accounts.invites.table.delete')}
                </Button>
              </Modal.Footer>
            </Modal>

            <Stack gap={1} direction={'horizontal'}>
              <OverlayTrigger
                delayShow={1000}
                overlay={
                  <Tooltip>
                    {t('accounts.invites.table.edit_tooltip')}
                  </Tooltip>
                }
              >
              <Button
                size="sm"
                variant="warning"
                className="text-center d-flex justify-content-center badge"
                onClick={() =>
                  navigate(PATH_PAGE.edit_invite.replace(':uuid', row.uuid))
                }
              >
                <i className="fa-duotone fa-pencil text-center fs-5 p-0"></i>
              </Button>
              </OverlayTrigger>

              <OverlayTrigger
                delayShow={1000}
                overlay={
                  <Tooltip>
                    {t('accounts.invites.table.delete_tooltip')}
                  </Tooltip>
                }
              >
              <Button
                size="sm"
                variant="danger"
                className="text-center d-flex justify-content-center badge"
                onClick={() => setShow(true)}
              >
                <i className="fa-duotone fa-trash fs-5 p-0"></i>
              </Button>
              </OverlayTrigger>
            </Stack>
          </>
        );
      }
    }
  ].filter((item) => item.show);

  // number of rows per page

  const handleRowsPerPageChange = (rowsNb) => {
    dispatch(changeRowsNumber(rowsNb));
  };
  // redux states
  const { isLoading, count, currentPageInvites, rowsNumber } = useSelector(
    (state) => state.invites
  );

  // initial load
  const [pageNumber, setPageNumber] = useState(1);
  // get selected page data
  const getSelectedPageData = (pageNumber) => {
    dispatch(getInvitesForCurrentPage(pageNumber));
    setPageNumber(pageNumber);
  };
  const [search, setSearch] = useState('');

  // for search
  const isMountedRef = useRef(false);
  useEffect(() => {
    if (isMountedRef.current === false) {
      isMountedRef.current = true;
      return;
    }
    const getData = setTimeout(() => {
      dispatch(searchForInvites(search));
    }, 500);
    return () => clearTimeout(getData);
  }, [search]);

  const listRef = useRef(null);
  const subHeaderComponentMemo = useMemo(() => {
    const handleSearch = (e) => {
      setSearch(e.target.value);
    };
    return (
      <div className="d-flex justify-content-between w-100 py-5  align-items-center">
        <div>
          <h2 style={{ margin: 0, paddingLeft: 0 }}>
            {t('accounts.invites.table.invite')}
          </h2>
        </div>
        <div className="d-flex flex-row align-items-center">
          <div className="d-flex justify-content-end">
            <SearchField onChange={handleSearch} placeholder={t('search')} />
          </div>
        </div>
      </div>
    );
  }, [search, listRef]);
  const customStyles = {
    headCells: {
      style: {
        paddingLeft: '8px', // override the cell padding for head cells
        paddingRight: '8px',
        color: 'gray'
      }
    },
    table: {
      style: {
        color: 'red',
        backgroundColor: 'gray',
        boxShadow: 'none'
      }
    },
    rows: {
      style: {
        paddingTop: 10,
        paddingBottom: 10,
        '&:not(:last-of-type)': {
          borderBottomStyle: 'dashed'
        }
      }
    },
    headRow: {
      style: {
        paddingTop: 10,
        paddingBottom: 10,

        borderBottomStyle: 'dashed'
      }
    },
    pagination: {
      style: {
        borderTopStyle: 'dashed'
      }
    }
  };
  const handleSort = (row, d) => {
    dispatch(
      getInvites({
        ordering: `${d === 'asc' ? '-' : ''}${row.key}`
      })
    );
    console.log(row, d);
  };

  return (
    <>
      <Toaster />
      <DataTable
        onSort={handleSort}
        sortServer
        columns={columns}
        data={currentPageInvites}
        subHeader
        subHeaderComponent={subHeaderComponentMemo}
        pagination
        paginationServer
        paginationTotalRows={count}
        onChangePage={getSelectedPageData}
        paginationComponentOptions={{
          rangeSeparatorText: t('accounts.invites.table.of'),
          rowsPerPageText: t('accounts.invites.table.members_per_page')
        }}
        progressPending={isLoading}
        paginationPerPage={rowsNumber}
        onChangeRowsPerPage={handleRowsPerPageChange}
        progressComponent={<IsLoadingIndicator />}
        paginationRowsPerPageOptions={[5, 10, 15, 20]}
        customStyles={customStyles}
        noTableCard
        noDataComponent={<T keyName="no_invites_available"></T>}
      />
    </>
  );
}

InvitesTable.propTypes = {
  type: PropTypes.string
};
